import { useFormContext } from 'react-hook-form';
import { ApplicantDataApplicantTabPanelSection } from '../ApplicationDataApplicantTabPanelLayout';
import { useLoanApplication } from '@axo/shared/data-access/hooks';
import { useApplicationDataStore } from '../../../../store/applicationDataStore';
import { Button } from '@blueprintjs/core';
import { DebtCategory } from './DebtCategory';
import { useEffect, useState } from 'react';
import { ApplicationDataFormValues } from '../../ApplicationDataForm/ApplicationDataFormSchema';
import { DebtType, Product } from '@axo/shared/data-access/types';
import { NetDebtItemDialog } from './NewDebtItemDialog';
import { ApplicationConditionalWrapper } from '../../../ConditionalWrappers/ApplicationConditionalWrapper';
import { MarketCountryCode } from '@axo/shared/types';
import { ApplicationDataCurrencyField } from '../../Fields/ApplicationDataCurrencyField';

interface DebtSectionProps {
  applicationID: string;
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
}

export const DebtSection: React.FC<DebtSectionProps> = ({
  applicationID,
  coApplicantCustomerID,
  coApplicantPersonID,
}) => {
  const isCoApplicant = coApplicantCustomerID || coApplicantPersonID;
  const fieldArrayName: 'CoDebts' | 'Debts' = isCoApplicant
    ? 'CoDebts'
    : 'Debts';

  const { data: applicationData } = useLoanApplication(applicationID);
  const { isEditing } = useApplicationDataStore(applicationID);
  const { watch } = useFormContext<ApplicationDataFormValues>();
  const [debts] = watch([fieldArrayName]);
  const [newDebtItem, setNewDebtItem] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!isEditing && newDebtItem !== undefined) {
      setNewDebtItem(undefined);
    }
  }, [isEditing, newDebtItem]);

  const categories = Array.from(
    debts.reduce((set, { Type }) => {
      if (Type) {
        set.add(Type);
      }
      return set;
    }, new Set<DebtType>()),
  );

  if (!applicationData) return null;

  return (
    <ApplicantDataApplicantTabPanelSection title="Debt">
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="CurrentYearDebtOutstandingAmount"
            label="Outstanding debt amount this year"
          />
        </ApplicationConditionalWrapper>
      )}
      {categories.map((category) => (
        <DebtCategory
          category={category}
          key={category}
          applicationID={applicationID}
          coApplicantCustomerID={coApplicantCustomerID}
          coApplicantPersonID={coApplicantPersonID}
        />
      ))}
      {isEditing && (
        <>
          <Button
            onClick={() => {
              setNewDebtItem(Date.now());
            }}
          >
            Create debt item
          </Button>
          {newDebtItem && (
            <NetDebtItemDialog
              applicationID={applicationID}
              coApplicantCustomerID={coApplicantCustomerID}
              coApplicantPersonID={coApplicantPersonID}
              key={newDebtItem}
            />
          )}
        </>
      )}
    </ApplicantDataApplicantTabPanelSection>
  );
};
